import { POLL_INTERVAL_MS } from '../config.js';
import { fetchCurrentProgram } from '../api/program.js';
import { ApiError } from '../api/client.js';
import { handleSessionRevoked } from '../api/session-revoked.js';
import { saveManifest, loadManifest } from '../storage/indexed-db.js';
import { evictIfNeeded } from '../storage/eviction.js';
import { diffPrograms, extractMediaUrls } from './diff.js';
import { setProgram, getProgram, syncDisplayProfileFromPoll } from '../state/program-store.js';
import { normalizeProgram } from './program-version.js';
import { resumeRotation, startRotation } from '../player/rotation.js';
import { prefetchMediaUrls } from '../storage/prefetch-media.js';
import { stableMediaCacheKey } from '../storage/media-cache-key.js';
import { nextBackoffMs, resetBackoffMs } from './backoff.js';
import { applyManifestPlayback } from './apply-manifest-playback.js';
import { applyProgramPlayback } from './apply-program-playback.js';
import { evaluatePlaybackFromManifest } from '../state/playback-status-store.js';
import { logInfo, logWarn } from '../state/log-store.js';
import {
  beginSync,
  endSync,
  setApiReachable,
  consumeReconnectPending,
  refreshConnectionDisplay,
} from '../state/connection-status-store.js';
import { clearInactiveReason } from '../state/playback-status-store.js';
import { renderPairingUI } from '../pairing/ui.js';

let timer = null;
let backoffMs = POLL_INTERVAL_MS;

/**
 * Start the 30s polling loop.
 * @param {{ immediate?: boolean }} [opts]
 *   `immediate: true` (default, backward-compat) runs a tick now then schedules.
 *   `immediate: false` skips the first tick — use when the caller already ran
 *   `syncProgramOnce()` on boot, to avoid a duplicate `current-program` fetch.
 */
export function startPolling(opts = {}) {
  stopPolling();
  if (opts.immediate === false) {
    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return;
  }
  tick();
}

export function stopPolling() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

/** Run a poll immediately (e.g. after browser `online` event). */
export function kickPoll() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  void tick();
}

/** One API sync without scheduling the next poll (boot / manual refresh path). */
export async function syncProgramOnce(opts = {}) {
  return runPollCycle({ forceRefresh: opts.forceRefresh === true, scheduleNext: false });
}

/**
 * @param {{ forceRefresh?: boolean; scheduleNext?: boolean }} opts
 */
async function runPollCycle(opts = {}) {
  const forceRefresh = opts.forceRefresh === true;
  const scheduleNext = opts.scheduleNext !== false;
  let nextDelay = POLL_INTERVAL_MS;
  beginSync();

  try {
    if (!navigator.onLine) {
      const playerEl = document.getElementById('player');
      await applyManifestPlayback(playerEl);
      logInfo('Offline — using IndexedDB manifest with local schedule check');
      backoffMs = resetBackoffMs();
      return;
    }

    const raw = await fetchCurrentProgram();
    const program = normalizeProgram(raw);
    const playerEl = document.getElementById('player');
    const playback = evaluatePlaybackFromManifest(program);

    if (!playback.shouldPlay) {
      setApiReachable(true);
      logInfo('Program inactive', { reason: playback.inactiveReason ?? program.inactiveReason });
      await applyProgramPlayback(program, playerEl, undefined, { prefetch: true });
      backoffMs = resetBackoffMs();
      return;
    }

    const cached = await loadManifest();
    const diff = diffPrograms(cached, program);
    const reconnected = consumeReconnectPending();

    await saveManifest(program);

    if (diff.versionChanged || forceRefresh || reconnected) {
      if (reconnected && !diff.versionChanged && !forceRefresh) {
        logInfo('Back online — refreshing program from API', {
          version: program.currentVersion,
          slots: program?.slots?.length ?? 0,
        });
        await prefetchMediaUrls(extractMediaUrls(program));
      } else {
        logInfo('Program version changed', {
          from: cached?.currentVersion,
          to: program.currentVersion,
          slots: program?.slots?.length ?? 0,
        });
        await prefetchMediaUrls(diff.newMediaUrls);
      }
      const pinned = new Set(extractMediaUrls(program).map(stableMediaCacheKey));
      await evictIfNeeded(pinned);
      clearInactiveReason();
      setProgram(program);
      if (playerEl && getProgram()?.slots?.length) {
        if (forceRefresh || reconnected) {
          if (!diff.versionChanged) resumeRotation();
          else startRotation(playerEl);
        }
      }
    } else if (!getProgram()?.slots?.length && program?.slots?.length) {
      logInfo('Poll hydrate (store empty)', {
        version: program?.currentVersion,
        slots: program?.slots?.length ?? 0,
      });
      clearInactiveReason();
      setProgram(program);
      if (playerEl && getProgram()?.slots?.length) startRotation(playerEl);
    } else {
      syncDisplayProfileFromPoll(program);
      void prefetchMediaUrls(extractMediaUrls(program));
    }
    setApiReachable(true);
    refreshConnectionDisplay();
    backoffMs = resetBackoffMs();
  } catch (err) {
    if (err instanceof ApiError && err.code === 'DEVICE_DISCONNECTED') {
      void handleSessionRevoked();
      return;
    }

    if (err instanceof ApiError && err.code === 'NO_ACTIVE_PROGRAM') {
      setApiReachable(true);
      logInfo('No assignment configured — showing pairing');
      setProgram(null);
      const playerEl = document.getElementById('player');
      if (playerEl) renderPairingUI(playerEl);
      backoffMs = resetBackoffMs();
      nextDelay = POLL_INTERVAL_MS;
      return;
    }

    const playerEl = document.getElementById('player');
    if (!navigator.onLine) {
      await applyManifestPlayback(playerEl);
      logInfo('Poll skipped API (offline) — using IndexedDB manifest', { error: err.message });
      setApiReachable(false);
      backoffMs = resetBackoffMs();
      nextDelay = POLL_INTERVAL_MS;
      return;
    }

    const hydrated = await applyManifestPlayback(playerEl);
    if (hydrated) {
      logInfo('Poll failed — using IndexedDB manifest', { error: err.message });
      void prefetchMediaUrls(extractMediaUrls(getProgram()));
      setApiReachable(false);
      backoffMs = resetBackoffMs();
      nextDelay = POLL_INTERVAL_MS;
      return;
    }

    setApiReachable(false);
    logWarn('Poll failed (no cached manifest)', {
      error: err.message,
      offline: !navigator.onLine,
      nextBackoffMs: backoffMs,
    });
    nextDelay = nextBackoffMs(backoffMs);
    backoffMs = nextDelay;
  } finally {
    endSync();
    if (scheduleNext) {
      timer = setTimeout(tick, nextDelay);
    }
  }
}

/**
 * @param {{ forceRefresh?: boolean }} [opts]
 */
function tick(opts = {}) {
  return runPollCycle({ ...opts, scheduleNext: true });
}

export async function forceSync() {
  stopPolling();
  await runPollCycle({ forceRefresh: true, scheduleNext: false });
  startPolling();
}
